import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * One action, one cell of the strip.
 *
 * The five were loose buttons in a row, which works in English and does not in French:
 * "Réinitialisation partielle" and "Réinitialisation complète" are about 190px each, so the row
 * wrapped — and wrapped again in the narrow rail. As equal cells each label owns its own share of
 * the width, wraps to a second line inside its own cell if it must, and the strip stays one strip.
 *
 * The whole cell is the target, not a small button inside it: at this size the label is the button.
 */
export const deviceActionSx: SxProps<Theme> = {
    height: 'auto',
    minHeight: '58px',
    px: 1.5,
    py: 1.25,
    borderRadius: 0,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: '0.01em',
    textTransform: 'none',
    textAlign: 'center',
    color: theme.palette.neutral[700],
    background: theme.palette.common.white,
    boxShadow: 'none',
    flexDirection: 'column',
    gap: 0.5,
    borderRight: `1px solid ${theme.palette.neutral[100]}`,
    // `last-child`, not `last-of-type`: the two link cells render as <a> (Button component=NavLink)
    // and the rest as <button>, so "last of type" was the QR cell — and it lost the divider that
    // should have separated it from Configurations, while the real last cell kept one.
    '&:last-child': {borderRight: 'none'},
    '& .MuiButton-startIcon': {
        margin: 0,
        '& .MuiSvgIcon-root': {fontSize: '1.125rem'},
    },
    '&:hover': {
        background: theme.palette.neutral[50],
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        color: theme.palette.neutral[400],
        background: theme.palette.neutral[50],
    },
};

/**
 * The QR menu. A code is glued to one socket, so the choice has to be about sockets rather than
 * about list positions: each row leads with the number that will end up printed on the sticker, and
 * carries the connector's current state so the person at the charger can tell which bay is which
 * without walking round to read the labels.
 */
export const qrMenuPaperSx: SxProps<Theme> = {
    mt: 0.75,
    minWidth: '272px',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.neutral[100]}`,
    boxShadow: '0 6px 24px rgba(16, 42, 62, 0.10)',
    '& .MuiList-root': {py: 0.75},
};

export const qrMenuTitleSx: SxProps<Theme> = {
    px: 1.75,
    pt: 0.5,
    pb: 1,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const qrMenuItemSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    mx: 0.75,
    px: 1,
    py: 1,
    borderRadius: '8px',
    '&:hover': {background: theme.palette.neutral[50]},
};

/** The connector number, set as the sticker shows it rather than as a line of text. */
export const qrConnectorBadgeSx: SxProps<Theme> = {
    width: '26px',
    height: '26px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: theme.palette.primary.dark,
    background: theme.palette.primaryColor[100],
};

export const qrConnectorLabelSx: SxProps<Theme> = {
    flex: 1,
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.neutral[700],
    whiteSpace: 'nowrap',
};
